import { useCallback, useEffect, useRef, useState } from 'react';
import { useTokens } from './tokens';

const HEADER_HEIGHT = 36;

/**
 * Separador arrastável entre colunas. Renderizado no canto direito de cada
 * header cell. Características:
 *
 *  - Drag horizontal redimensiona a coluna (entrega novo width via `onResize`).
 *  - Duplo clique aciona autofit. A zona do duplo clique determina qual:
 *      - Dentro do header (clientY <= headerBottom) → `onAutofitHeader`
 *      - Fora do header (linhas)                    → `onAutofitContent`
 *
 *  - Visualmente é um traço fino (4px hit, 1px visível); durante drag/hover
 *    estende uma linha sutil pra baixo cobrindo as linhas de dados — feedback
 *    de "estou redimensionando ESSA coluna".
 *
 *  - Quando o usuário pressiona Shift durante o duplo clique, força a zona
 *    HEADER (atalho útil quando a célula não tem conteúdo medível).
 */
export function ResizeHandle({ onResize, onAutofit, initialWidth }) {
    const T = useTokens();
    const [dragging, setDragging] = useState(false);
    const [hovering, setHovering] = useState(false);
    const startRef = useRef({ x: 0, w: 0 });

    const handleMouseDown = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            startRef.current = { x: e.clientX, w: initialWidth };
            setDragging(true);
        },
        [initialWidth],
    );

    useEffect(() => {
        if (!dragging) {
            return undefined;
        }
        const handleMove = (e) => {
            const delta = e.clientX - startRef.current.x;
            onResize(startRef.current.w + delta);
        };
        const handleUp = () => setDragging(false);
        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);
        const prevCursor = document.body.style.cursor;
        const prevSelect = document.body.style.userSelect;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        return () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
            document.body.style.cursor = prevCursor;
            document.body.style.userSelect = prevSelect;
        };
    }, [dragging, onResize]);

    const handleDoubleClick = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Duplo clique no header = autofit ÚNICO combinado: o caller
            // (useResizeIntegration.onAutofit) calcula max(header, content) e
            // limita a 160px. Sem mais zonas/gestos distintos — uma ação só.
            onAutofit?.();
        },
        [onAutofit],
    );

    // Quando dragging OU hover, a linha estende pra baixo (visual feedback).
    const showExtendedLine = dragging || hovering;

    return (
        <>
            {/* Hit area do header (4px x 36px) — cursor col-resize + cliques aqui
                detectam zona "header" pro duplo clique. Sentinel SEMPRE clicável
                (não depende de hover). */}
            {/* biome-ignore lint/a11y/useSemanticElements: <hr> não aceita interatividade nem aria-valuenow; precisamos de role=separator interativo. */}
            <div
                role="separator"
                aria-orientation="vertical"
                aria-valuenow={Math.round(initialWidth)}
                aria-valuemin={50}
                aria-valuemax={600}
                aria-label="Redimensionar coluna"
                tabIndex={-1}
                onMouseDown={handleMouseDown}
                onDoubleClick={handleDoubleClick}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                title="Arraste para redimensionar · duplo clique para ajustar auto."
                style={{
                    position: 'absolute',
                    top: 0,
                    right: -2,
                    width: 4,
                    height: HEADER_HEIGHT,
                    cursor: 'col-resize',
                    zIndex: 6,
                    background: dragging ? T.accent : 'transparent',
                    transition: dragging ? 'none' : 'background 0.1s',
                }}
            />
            {/* Linha guia visual estendendo pra baixo durante drag/hover.
                `pointer-events: none` impede que vire hit-area; assim NÃO
                contribui pra scrollHeight (causa raiz do scroll infinito
                que existia com o body hit-area antigo).  */}
            {showExtendedLine && (
                <div
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        top: HEADER_HEIGHT,
                        right: -0.5,
                        width: 1,
                        height: 100,
                        background: dragging ? T.accent : T.lineMid,
                        opacity: dragging ? 0.8 : 0.4,
                        pointerEvents: 'none',
                        zIndex: 5,
                    }}
                />
            )}
        </>
    );
}
